import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const DiscountCodeField = ({
  discountCode,
  discountCodeValidation,
  discountError,
  isValidatingDiscount,
  onDiscountCodeChange,
  onValidateDiscountCode,
  onClearDiscountCode,
}) => {
  const isApplied = Boolean(discountCodeValidation && discountCodeValidation.is_valid);
  const [isOpen, setIsOpen] = useState(
    () => Boolean(discountError) || Boolean(String(discountCode || '').trim()) || isApplied
  );
  const inputRef = useRef(null);

  useEffect(() => {
    if (discountError || isApplied) setIsOpen(true);
  }, [discountError, isApplied]);

  const handleOpen = () => {
    setIsOpen(true);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleCancel = () => {
    onClearDiscountCode();
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <div className="discount-code-section discount-code-section--summary">
        <button type="button" className="discount-code-text-button discount-code-apply" onClick={handleOpen}>
          <img src="/manual-booking/icons/discount-ticket.png" alt="" className="discount-code-tag-icon" />
          <span className="discount-code-text-button-label">Apply discount code</span>
        </button>
      </div>
    );
  }

  return (
    <div className="discount-code-section discount-code-section--summary discount-code-apply">
      <div className="discount-code-heading">
        <label className="form-label" htmlFor="discount-code">
          Enter discount code
        </label>
        <button type="button" className="discount-code-text-button" onClick={handleCancel}>
          Cancel
        </button>
      </div>
      <div className="discount-code-input-group">
        <div className={`input-wrapper ${discountError ? 'error' : ''}`}>
          <input
            id="discount-code"
            ref={inputRef}
            type="text"
            value={discountCode}
            onChange={(event) => onDiscountCodeChange(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                if (!isApplied && discountCode.trim() && !isValidatingDiscount) {
                  onValidateDiscountCode();
                }
              }
            }}
            placeholder="SAVE10"
            className="form-input"
            disabled={isValidatingDiscount || isApplied}
          />
          {isApplied ? (
            <button
              type="button"
              onClick={onClearDiscountCode}
              className="discount-remove-button"
            >
              Remove
            </button>
          ) : (
            <button
              type="button"
              onClick={onValidateDiscountCode}
              disabled={isValidatingDiscount || !discountCode.trim()}
              className="discount-apply-button"
              aria-label={isValidatingDiscount ? 'Checking discount code' : 'Apply discount code'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M14.43 5.93L20.5 12L14.43 18.07" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M3.5 12H20.33" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
      {discountError ? <span className="error-message">{discountError}</span> : null}
      {isApplied ? (
        <span className="discount-code-success">Discount code applied</span>
      ) : null}
    </div>
  );
};

DiscountCodeField.propTypes = {
  discountCode: PropTypes.string,
  discountCodeValidation: PropTypes.shape({
    is_valid: PropTypes.bool,
  }),
  discountError: PropTypes.string,
  isValidatingDiscount: PropTypes.bool,
  onDiscountCodeChange: PropTypes.func,
  onValidateDiscountCode: PropTypes.func,
  onClearDiscountCode: PropTypes.func,
};

DiscountCodeField.defaultProps = {
  discountCode: '',
  discountCodeValidation: null,
  discountError: '',
  isValidatingDiscount: false,
  onDiscountCodeChange: () => {},
  onValidateDiscountCode: () => {},
  onClearDiscountCode: () => {},
};

export default DiscountCodeField;
